'use client'
import { BulkEditOverlay } from '@/app/components/bulk-edit-overlay'
import FavoriteButton from '@/components/favorite-button'
import { useCheckFavorite } from '@/context/favorites-manager'
import UserContext from '@/context/user-context'
import { cn } from '@/util/cn'
import { canEdit } from '@/util/user-permissions'
import { getUserTypeColor } from '@/util/user-type-colors'
import { GifOverlayProps } from '@giphy/react-components'
import { usePathname } from 'next/navigation'
import { useContext } from 'react'
import TagOverlay from 'ui/src/components/overlay/tag-overlay'
import { useBulkEditOpen } from '../bulk-edit-state'
import { AttrOverlay } from './attr-overlay'
import AudioOverlay from './audio-overlay'
import ClipboardButton from './clipboard-button'
import EditButton from './edit-button'
import PrivacyOverlay from './privacy-overlay'

const HoverOverlayContainerTW: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="absolute top-0 left-0 h-full w-full">{children}</div>
)

const GPSTagOverlayTW: React.FC<{ tag: string }> = ({ tag }) => (
    <a
        className="absolute top-1/2 left-1/2 h-9 max-w-full -translate-x-1/2 -translate-y-1/2 transform overflow-hidden rounded-full bg-giphyBlack px-[18px] text-[15px] leading-9 font-bold text-ellipsis whitespace-nowrap text-giphyGreen italic"
        onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            location.href = '/edit/gifs?query=' + encodeURIComponent(tag)
        }}
    >
        #{tag}
    </a>
)

const TrendingIndicator = ({ colorClass }: { colorClass: string }) => (
    <div className={`absolute top-2 left-2 h-3.5 w-3.5 rounded-full ${colorClass}`} />
)

type UserTypeBorderProps = {
    user?: GifOverlayProps['gif']['user'] & { is_community?: boolean }
}

const UserTypeBorder = ({ user }: UserTypeBorderProps) => {
    const color = getUserTypeColor(user?.user_type, user?.is_community)

    return color ? (
        <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-1"
            style={{ backgroundColor: color }}
        />
    ) : null
}

const GifOverlay = ({ isHovered, gif, width, hideAttribution }: GifOverlayProps & { hideAttribution?: boolean }) => {
    const { user } = useContext(UserContext)
    const isFavorite = useCheckFavorite(isHovered ? (gif.id as string) : undefined)
    const pathname = usePathname()
    const bulkEditEnabled = useBulkEditOpen()
    const showUserTypeBorder = bulkEditEnabled || pathname.includes('/edit/gifs')
    const hasGpsTag = Array.isArray(gif.gps_tags) && gif.gps_tags.length > 0 && typeof gif.gps_tags[0] === 'string'

    let trendingIcon = null
    if (Array.isArray(gif.tags)) {
        if (gif.tags.includes('giphytrending')) {
            trendingIcon = <TrendingIndicator colorClass="bg-giphyGreen" />
        } else if (gif.tags.includes('giphytrendingqueue')) {
            trendingIcon = <TrendingIndicator colorClass="bg-blue-500" />
        } else if (gif.tags.includes('giphytrendingschedule')) {
            trendingIcon = <TrendingIndicator colorClass="bg-yellow-400" />
        }
    }

    return bulkEditEnabled ? (
        <>
            <AudioOverlay gif={gif} isHovered={isHovered} width={width} />
            <PrivacyOverlay gif={gif} />
            <BulkEditOverlay isHovered={isHovered} gif={gif} width={width} />
            {showUserTypeBorder && <UserTypeBorder user={gif.user} />}
        </>
    ) : (
        <>
            <AudioOverlay gif={gif} isHovered={isHovered} width={width} />
            {!isHovered && trendingIcon}
            {isHovered && hasGpsTag ? (
                <HoverOverlayContainerTW>
                    <div className="absolute top-2.5 right-2.5 flex items-center gap-3 rounded-md bg-giphyBlack/10 p-2">
                        {user && canEdit(user, gif) && <EditButton gif={gif} />}
                        <FavoriteButton
                            hideLabel
                            gif={gif}
                            className={cn(
                                'size-5 fill-giphyWhite opacity-5 duration-300 ease-in-out',
                                (isFavorite !== undefined || !user) && 'opacity-100'
                            )}
                            iconClassName="hover:scale-110 transition-all"
                            isFavorite={!!isFavorite}
                        />
                        <ClipboardButton gif={gif} />
                    </div>
                    {gif.gps_tags && <GPSTagOverlayTW tag={gif.gps_tags[0]} />}
                </HoverOverlayContainerTW>
            ) : (
                isHovered && (
                    <div className="absolute top-2.5 right-2.5 flex items-center gap-3 rounded-md bg-giphyBlack/10 p-2">
                        {user && canEdit(user, gif) && <EditButton gif={gif} />}
                        <FavoriteButton
                            hideLabel
                            gif={gif}
                            className={cn(
                                'size-5 fill-giphyWhite opacity-5 duration-300 ease-in-out',
                                (isFavorite !== undefined || !user) && 'opacity-100'
                            )}
                            iconClassName="hover:scale-110 transition-all"
                            isFavorite={!!isFavorite}
                        />
                        <ClipboardButton gif={gif} />
                    </div>
                )
            )}
            {!hideAttribution && (
                <>
                    {gif.user && pathname.indexOf(`/${gif.user.username}`) !== 0 ? (
                        <AttrOverlay gif={gif} width={width} isHovered={isHovered} />
                    ) : (
                        <TagOverlay gif={gif} isHovered={isHovered} width={width} />
                    )}
                </>
            )}
            <PrivacyOverlay gif={gif} />
            {showUserTypeBorder && <UserTypeBorder user={gif.user} />}
        </>
    )
}
export default GifOverlay
